import React, { createContext, useContext, useState, useEffect } from "react";
import type { AuthenticatedUser } from "../types.ts";
import { supabase } from "../src/lib/supabaseClient";

export type AuthStatus =
  "UNAUTHENTICATED" | "HYDRATING" | "AUTHENTICATED_ORPHAN" | "READY";

interface AuthContextType {
  user: AuthenticatedUser | null;
  token: string | null;
  authStatus: AuthStatus;
  login: (
    provider: "google" | "apple" | "email",
    credentials?: any,
  ) => Promise<{ success: boolean; message: string }>;
  signUp: (
    provider: "google" | "apple" | "email",
    data?: any,
  ) => Promise<{ success: boolean; message: string; code?: string }>;
  verifyEmail: (
    email: string,
    token: string,
    password?: string,
  ) => Promise<{ success: boolean; message: string; code?: string }>;
  resendOtp: (
    email: string,
  ) => Promise<{ success: boolean; message: string; code?: string }>;
  provisionWorkspace: (
    data: any,
  ) => Promise<{ success: boolean; message: string; code?: string }>;
  refreshSession: () => Promise<void>;
  /** Force an immediate identity reconciliation check */
  syncIdentity: () => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isProfileLoading: boolean;
  /** True while the database is actively verifying/hydrating the user profile. */
  isSyncing: boolean;
  lastError: Error | null;
  retryProfileFetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

/* ===============================
   USER MAPPING (Anti-Spoofing Hardened)
   =============================== */
export const mapSupabaseUser = (sbUser: any): AuthenticatedUser => {
  const meta = sbUser?.user_metadata || {};
  const appMeta = sbUser?.app_metadata || {};
  return {
    id: sbUser?.id || "",
    email: sbUser?.email || "",
    firstName: meta.firstName || "User",
    lastName: meta.lastName || "",
    avatarUrl:
      meta.avatarUrl ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(meta.firstName || "U")}&background=random`,
    title: meta.title || "User",
    // 🔒 SECURITY BOUNDARY: Role and Tenancy MUST come exclusively from app_metadata (server-controlled)
    // user_metadata is user-writable via supabase.auth.updateUser() and must never be trusted for access control.
    role: appMeta.role || "client",
    nano_id: appMeta.nano_id || meta.nano_id,
    is_signed_up: true,
    is_email_verified: !!sbUser?.email_confirmed_at,
    is_logged_in: true,
    onboarding_status: meta.onboarding_status || "not_started",
    activation_status: meta.activation_status || "inactive",
    subscription_status: appMeta.subscription_status || "pending",
    client_nano_id: appMeta.client_nano_id,
    client_short_id: appMeta.client_short_id,
    client_slug: appMeta.client_slug,
    client_id: appMeta.client_id,
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastError, setLastError] = useState<Error | null>(null);

  // REFRESH STORM PROTECTION
  const lastRefreshTime = React.useRef<number>(0);
  const REFRESH_COOLDOWN = 5000; // 5 seconds

  /* ===============================
     ENTERPRISE SAFE FETCH
     =============================== */
  const safeFetch = async (
    url: string,
    options: RequestInit,
    timeoutMs = 15000,
  ) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    let response: Response;

    try {
      // AUTO-INJECT TOKEN
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const headers = new Headers(options.headers || {});

      // CLIENT-SIDE AUTH GUARD: Explicit whitelist of public auth routes that do NOT
      // require a Bearer token. Everything else under /api/ is protected.
      // IMPORTANT: /api/auth/provision-workspace is intentionally NOT in this list
      // because it requires a valid session to identify the provisioning user.
      const PUBLIC_AUTH_ROUTES = [
        "/api/auth/signup-otp",
        "/api/auth/verify-otp",
        "/api/auth/resend-otp",
        "/api/auth/login",
      ];
      const isProtectedRoute =
        url.startsWith("/api/") &&
        !PUBLIC_AUTH_ROUTES.some((route) => url.startsWith(route)) &&
        !url.startsWith("/api/public");

      if (isProtectedRoute && !session?.access_token) {
        console.warn(
          "[SafeFetch] Blocked request to protected route without token:",
          url,
        );
        throw Object.assign(
          new Error("Authentication required. Please log in."),
          { code: "CLIENT_UNAUTHORIZED", url },
        );
      }

      if (session?.access_token) {
        headers.set("Authorization", `Bearer ${session.access_token}`);
      }

      response = await fetch(url, {
        ...options,
        headers, // Use the new headers object
        credentials: "include",
        signal: controller.signal,
      });
    } catch (err: any) {
      if (err.name === "AbortError") {
        throw Object.assign(new Error("Service timeout"), {
          code: "SERVICE_UNAVAILABLE",
        });
      }
      throw err;
    } finally {
      clearTimeout(timeout);
    }

    const contentType = response.headers.get("content-type");
    let data: any;

    try {
      if (contentType?.includes("application/json")) {
        data = await response
          .json()
          .then((j) => (j?.data !== undefined ? j.data : j));
      } else {
        const text = await response.text();
        try {
          data = JSON.parse(text);
          console.warn("[SafeFetch] Non-JSON header but valid JSON body");
        } catch {
          console.error(
            "[SafeFetch] HTML leak or invalid JSON detected:",
            text.slice(0, 500),
          );
          throw new Error(
            `Server returned invalid response (${response.status}). Potential HTML leak or system crash. Check server logs.`,
          );
        }
      }
    } catch (parseError: any) {
      console.error("[SafeFetch] Parse Error:", parseError.message);
      throw new Error(
        `Failed to parse server response as JSON: ${parseError.message}`,
      );
    }

    if (!response.ok) {
      // ResponseHandler.error() contract: { success: false, error: { code, message } }
      const errorPayload = data?.error || {};
      const error = new Error(
        errorPayload.message || data?.message || "Request failed",
      );
      (error as any).code =
        errorPayload.code || data?.error_code || "UNKNOWN_ERROR";
      (error as any).requestId =
        errorPayload.correlation_id || data?.request_id;
      throw error;
    }

    return data;
  };

  /* ===============================
     SESSION BOOTSTRAP
     =============================== */
  const fetchEnhancedProfile = async (userId: string) => {
    try {
      // Step 1: Fetch User Profile (including client_id)
      let { data: userProfile, error: userError } = await supabase
        .from("users")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (userError || !userProfile) {
        // DEF-01: On 42501 permission denial, probe a silent session refresh first
        // before executing permanent session invalidation.
        if (userError?.code === "42501") {
          console.warn(
            "[Auth] 42501 permission error on profile fetch. Attempting token refresh probe...",
          );
          try {
            const { data: refreshData, error: refreshError } =
              await supabase.auth.refreshSession();
            if (!refreshError && refreshData?.session?.user) {
              const { data: retryProfile, error: retryError } = await supabase
                .from("users")
                .select("*")
                .eq("id", userId)
                .maybeSingle();
              if (!retryError && retryProfile) {
                console.info(
                  "[Auth] Profile fetch recovered successfully following session refresh.",
                );
                userProfile = retryProfile;
                userError = null;
              }
            }
          } catch (refreshEx) {
            console.error(
              "[Auth] Session refresh attempt failed on 42501:",
              refreshEx,
            );
          }
        }

        // ⚡ FAIL-FAST: 42501 = confirmed persistent permission denied after refresh probe.
        if (userError?.code === "42501") {
          console.error(
            "[Auth] Permission denied on profile fetch. Clearing session.",
            {
              userId,
              code: userError.code,
            },
          );

          // DEF-02: Controlled session cleanup without hard browser reload loop
          try {
            await supabase.auth.signOut();
          } catch (signOutErr) {
            console.error(
              "[AuthProvider] SignOut error during 42501 cleanup:",
              signOutErr,
            );
            try {
              localStorage.removeItem("sb-xduawymidagalhwajlqo-auth-token");
            } catch (storageErr) {
              console.error(
                "[AuthProvider] Failed to clear verified auth key:",
                storageErr,
              );
            }
          }
          setUser(null);
          setToken(null);
          setLastError(
            new Error(
              "Database permission denied. Please sign in with an authorized account.",
            ),
          );
          return null;
        }

        console.warn(
          "AuthProvider: fetchEnhancedProfile (User) failed",
          JSON.stringify(userError),
        );
        return null;
      }

      // Removed debug log

      let clientData = null;

      // Step 2: Fetch Client Data (if client_id exists)
      if (userProfile.client_id) {
        const { data: client, error: clientError } = await supabase
          .from("clients")
          .select("slug, nano_id, short_id")
          .eq("id", userProfile.client_id)
          .maybeSingle();

        if (clientError) {
          console.error(
            "AuthProvider: fetchEnhancedProfile (Client) failed",
            clientError,
          );
        } else {
          clientData = client;
        }
      }

      // Removed debug log

      return {
        nano_id: userProfile.nano_id,
        client_slug: clientData?.slug,
        client_nano_id: clientData?.nano_id,
        client_short_id: clientData?.short_id,
        onboarding_status: userProfile.onboarding_status,
        subscription_status: userProfile.subscription_status,
        role: userProfile.role, // Sync Role from DB Priority
        client_id: userProfile.client_id,
      };
    } catch (err) {
      console.error("Error fetching enhanced profile:", err);
      return null;
    }
  };

  // Helper to fetch and merge profile
  const refreshUser = async (supabaseUser: any) => {
    if (!supabaseUser) {
      setUser(null);
      setToken(null);
      setIsLoading(false);
      setIsProfileLoading(false);
      return;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session) {
      setToken(session.access_token);
    }

    // 1. Immediate optimistic update from JWT/Metadata
    // NOTE: If we already have a user in state with a valid role, we SKIP the optimistic
    // update to prevent "Role Flipping" where the UI briefly reverts to stale metadata.
    const initialUser = mapSupabaseUser(supabaseUser);

    // Use raw 'user' state from closure/state. If it matches ID, we skip optimistic update
    // and DO NOT toggle isProfileLoading to prevent UI flicker on background revalidation.
    if (!user || user.id !== initialUser.id) {
      setIsProfileLoading(true); // Signal: DB fetch starting for new session
      setUser(initialUser);
    }
    // Existing session: keep isProfileLoading as false so the active UI remains responsive.

    // 2. Adaptive Background Sync (Ghost Hydration)
    try {
      setLastError(null);
      setIsSyncing(true);

      // A3: Extended from 1000ms to 3000ms — matches the loadingTimeout safety valve.
      // A 1s timeout caused "ghost identity" loops on slow DB connections: DB profile
      // arrived late, user got stale JWT role, AppContent redirected to Onboarding.
      const dbProfileResult = await Promise.race([
        fetchEnhancedProfile(supabaseUser.id),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
      ]);

      let effectiveProfile = dbProfileResult;
      // If profile has no client_id yet and user is not admin, wait briefly and retry once
      // to allow PostgreSQL triggers to complete workspace provisioning on new signups.
      if (
        (!effectiveProfile || !effectiveProfile.client_id) &&
        !["admin", "super_admin"].includes(initialUser.role || "")
      ) {
        await new Promise((r) => setTimeout(r, 600));
        const retryProfile = await fetchEnhancedProfile(supabaseUser.id);
        if (retryProfile?.client_id) {
          effectiveProfile = retryProfile;
        }
      }

      if (effectiveProfile) {
        const finalUser = { ...initialUser, ...effectiveProfile };

        // Preserving existing user reference when relevant fields have not changed.
        // This stops cascading re-renders and WebSocket resubscription loops.
        setUser((prevUser) => {
          if (!prevUser) return finalUser;

          const hasChanged =
            prevUser.id !== finalUser.id ||
            prevUser.role !== finalUser.role ||
            prevUser.onboarding_status !== finalUser.onboarding_status ||
            prevUser.subscription_status !== finalUser.subscription_status ||
            prevUser.client_id !== finalUser.client_id ||
            prevUser.email !== finalUser.email ||
            prevUser.client_slug !== finalUser.client_slug ||
            prevUser.client_short_id !== finalUser.client_short_id ||
            prevUser.client_nano_id !== finalUser.client_nano_id ||
            prevUser.firstName !== finalUser.firstName ||
            prevUser.lastName !== finalUser.lastName;

          if (!hasChanged) {
            return prevUser; // Keep existing reference: ZERO re-renders triggered
          }
          return finalUser; // Genuine change: update state
        });

        // DEF-01: Adopt database profile as authoritative SSOT.
        // Do NOT call supabase.auth.refreshSession() here; that re-triggers onAuthStateChange
        // with stale JWT metadata, causing an infinite role-flipping auth storm.
        if (dbProfileResult && initialUser.role !== dbProfileResult.role) {
          console.info(
            "[AuthProvider] Role alignment: database profile adopted as SSOT.",
          );
        }
      }
    } catch (err: any) {
      setLastError(err);
    } finally {
      // SILENT COMPLETION
      setIsLoading(false);
      setIsProfileLoading(false);
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    // SAFETY VALVE: Permanent release after 3 seconds
    const loadingTimeout = setTimeout(() => {
      if (mounted && (isLoading || isProfileLoading || isSyncing)) {
        setIsLoading(false);
        setIsProfileLoading(false);
        setIsSyncing(false);
      }
    }, 3000);

    // 1. Initial Session Check
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        if (data.session?.user) {
          setToken(data.session.access_token);
          try {
            supabase.realtime.setAuth(data.session.access_token);
          } catch (_) {}
          refreshUser(data.session.user);
        } else {
          setToken(null);
          setIsLoading(false);
          setIsProfileLoading(false);
        }
      })
      .catch((err) => {
        console.error("[AuthProvider] Error getting session:", err);
        if (mounted) {
          setToken(null);
          setIsLoading(false);
          setIsProfileLoading(false);
        }
      });

    // 2. Auth State Listener
    const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (mounted) {
        if (session?.user) {
          setToken(session.access_token);
          try {
            supabase.realtime.setAuth(session.access_token);
          } catch (_) {}
          // Debounce / Consolidate updates by calling refreshUser
          refreshUser(session.user);
        } else {
          setUser(null);
          setToken(null);
          setIsLoading(false);
          setIsProfileLoading(false);
        }
      }
    });

    // 3. Multi-Session Focus Sync (RC2: debounced to prevent re-auth storm)
    // When switching tabs or returning to the app, check if the identity shifted.
    // Without debounce, rapid alt-tabs spawn dozens of concurrent DB calls.
    let focusDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    const handleFocus = () => {
      if (focusDebounceTimer) clearTimeout(focusDebounceTimer);
      focusDebounceTimer = setTimeout(() => {
        console.log(
          "[AuthProvider] App Focused: Checking session integrity...",
        );
        supabase.auth.getUser().then(({ data }) => {
          if (data.user) {
            refreshUser(data.user);
          } else {
            // User signed out in another tab
            setUser(null);
            setToken(null);
          }
        });
      }, 10000); // 10s debounce
    };
    window.addEventListener("focus", handleFocus);

    return () => {
      mounted = false;
      clearTimeout(loadingTimeout);
      if (focusDebounceTimer) clearTimeout(focusDebounceTimer);
      window.removeEventListener("focus", handleFocus);
      console.log("[AuthProvider] Cleaning up auth listener");
      data.subscription.unsubscribe();
    };
  }, []);

  /* ===============================
     AUTH ACTIONS
     =============================== */
  const refreshSession = async () => {
    console.log("[AuthProvider] Manual refresh requested");
    const { data, error } = await supabase.auth.refreshSession();
    if (error) throw error;
    if (data.session?.user) await refreshUser(data.session.user);
  };

  const syncIdentity = async () => {
    console.log("[AuthProvider] Syncing identity...");
    const { data } = await supabase.auth.getUser();
    if (data.user) await refreshUser(data.user);
  };

  const retryProfileFetch = async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) await refreshUser(data.session.user);
  };

  const login = async (
    provider: "google" | "apple" | "email",
    credentials?: any,
  ) => {
    setIsLoading(true);
    try {
      if (provider === "email") {
        const { error } = await supabase.auth.signInWithPassword(credentials);
        if (error) {
          // CRITICAL: Clear any stale session so AppContent doesn't auto-navigate
          // to Dashboard with an old user state, causing the onboarding loop
          await supabase.auth.signOut();
          setUser(null);
          return {
            success: false,
            message: error.message || "Invalid email or password",
          };
        }
        return { success: true, message: "Login successful" };
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams:
            provider === "apple"
              ? { scope: "name email" }
              : { access_type: "offline", prompt: "consent" },
        },
      });

      if (error) throw error;
      return { success: true, message: `Redirecting to ${provider}...` };
    } catch (err: any) {
      return { success: false, message: err.message || "Login failed" };
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (
    _provider: "google" | "apple" | "email",
    data?: any,
  ) => {
    setIsLoading(true);
    try {
      const res = await safeFetch(
        "/api/auth/signup-otp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
        30000,
      );
      // ResponseHandler.success() contract: { success: true, data: { message, ... } }
      return {
        success: true,
        message: res.data?.message || res.message || "OTP sent successfully",
      };
    } catch (err: any) {
      return { success: false, message: err.message, code: err.code };
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async (email: string) => {
    setIsLoading(true);
    try {
      const res = await safeFetch("/api/auth/resend-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      return {
        success: true,
        message: res.data?.message || res.message || "Code resent successfully",
      };
    } catch (err: any) {
      return { success: false, message: err.message, code: err.code };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmail = async (email: string, otp: string, password?: string) => {
    setIsLoading(true);
    try {
      const res = await safeFetch("/api/auth/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp, password }),
      });

      // OTP RACE GUARD: After backend verifies the OTP, attempt sign-in so the
      // Supabase session is available for provisionWorkspace. Supabase can have a
      // brief propagation delay — wrap in try/catch and flag sessionReady so the
      // caller can retry or show a holding state rather than failing silently.
      let sessionReady = false;
      if (password) {
        try {
          const { error: signInError } = await supabase.auth.signInWithPassword(
            { email, password },
          );
          if (signInError) {
            // Propagation delay is common — log and continue. provisionWorkspace
            // has its own 1.5s retry guard to handle this case.
            console.warn(
              "[verifyEmail] signInWithPassword delayed — session may not be ready yet:",
              signInError.message,
            );
          } else {
            sessionReady = true;
          }
        } catch (signInErr: any) {
          console.warn(
            "[verifyEmail] signInWithPassword threw unexpectedly:",
            signInErr.message,
          );
        }
      }

      return {
        success: true,
        sessionReady,
        message: res.data?.message || res.message || "Account verified",
      };
    } catch (err: any) {
      return { success: false, message: err.message, code: err.code };
    } finally {
      setIsLoading(false);
    }
  };

  const provisionWorkspace = async (data: any) => {
    setIsLoading(true);
    try {
      // SESSION GUARD: provision-workspace requires a valid Bearer token.
      // Wait up to 1.5s for Supabase session propagation after OTP verify before
      // calling the API. This handles the common timing gap between verifyEmail
      // completing and the Supabase session being available for safeFetch.
      const {
        data: { session: initialSession },
      } = await supabase.auth.getSession();
      if (!initialSession?.access_token) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        const {
          data: { session: retrySession },
        } = await supabase.auth.getSession();
        if (!retrySession?.access_token) {
          console.error(
            "[provisionWorkspace] No session available after 1.5s retry. User must re-authenticate.",
          );
          return {
            success: false,
            message: "Session expired. Please log in again.",
            code: "SESSION_EXPIRED",
          };
        }
      }

      const res = await safeFetch(
        "/api/auth/provision-workspace",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
        60000,
      ); // 60s timeout for heavy provisioning

      // Force token refresh to get updated client_id in metadata
      await syncIdentity();

      return {
        success: true,
        message: res.data?.message || res.message || "Workspace provisioned",
      };
    } catch (err: any) {
      return { success: false, message: err.message, code: err.code };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // 1. Immediate optimistic cleanup
    setUser(null);
    setToken(null);
    setIsLoading(true);

    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoading(false);
      // Optional: Clear any other local state if needed
      localStorage.removeItem("supabase.auth.token");
    }
  };

  const authStatus: AuthStatus = React.useMemo(() => {
    if (isLoading || isProfileLoading) {
      return "HYDRATING";
    }
    if (!user || !user.is_logged_in) {
      return "UNAUTHENTICATED";
    }
    const isAdmin = ["admin", "super_admin"].includes(user.role || "");
    if (!user.client_id && !isAdmin) {
      return "AUTHENTICATED_ORPHAN";
    }
    return "READY";
  }, [isLoading, isProfileLoading, user]);

  const value = React.useMemo(
    () => ({
      user,
      token,
      authStatus,
      login,
      signUp,
      verifyEmail,
      resendOtp,
      provisionWorkspace,
      refreshSession,
      syncIdentity,
      logout,
      isLoading,
      isProfileLoading,
      isSyncing,
      lastError,
      retryProfileFetch,
    }),
    [
      user,
      token,
      authStatus,
      isLoading,
      isProfileLoading,
      isSyncing,
      lastError,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
